// iPhone, игра открыта с иконки на рабочем столе (полноэкранный режим): iOS иногда отдаёт странице
// высоту экрана без зоны выреза («монобровь» / Dynamic Island) — нижнее меню повисает над краем, под
// ним чёрная полоса. Замеряем недостачу и растягиваем игру до низа экрана (--sk-ios-gap, --sk-dvh).
// Сверху iOS размывает край под часами и вырезом — шапка игры опускается ниже (класс ios-under-notch).
import { reportError } from './errors';

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPad с iPadOS представляется Mac — отличаем по сенсорному экрану
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true
  );
}

/** Безопасные зоны экрана (вырез сверху, полоска «домой» снизу) в CSS-пикселях. */
function safeInsets(): { top: number; bottom: number } {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;' +
    'padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)';
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const res = {
    top: Number.parseFloat(cs.paddingTop) || 0,
    bottom: Number.parseFloat(cs.paddingBottom) || 0,
  };
  probe.remove();
  return res;
}

/**
 * Сколько высоты недодал iOS: полный экран минус низ окна страницы (inner). Подгоняем, только когда страница
 * заходит под вырез (safeTop > 0) и недостача ровно размером с него — это и есть ошибка iOS. Если
 * вырез страницу не перекрывает (safeTop = 0), окно ниже часов и его высота верная — не трогаем.
 */
export function iosViewportGap(
  inner: number,
  innerW: number,
  screenW: number,
  screenH: number,
  safeTop: number,
): number {
  const portrait = inner >= innerW;
  const full = portrait ? Math.max(screenW, screenH) : Math.min(screenW, screenH);
  const gap = Math.round(full - inner);
  if (gap < 16 || gap > 90) return 0; // больше — открыта клавиатура
  return safeTop > 0 && Math.abs(gap - safeTop) <= 6 ? gap : 0;
}

/**
 * Где на самом деле оказывается нижний край для элементов, прижатых к низу (position: fixed; bottom: 0).
 * Мерим его, а не window.innerHeight: вернувшись из фона, iOS чинит высоту окна, а innerHeight и событие
 * resize иногда отстают — тогда старая поправка опускала меню за край экрана.
 */
function fixedBottom(): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;left:0;bottom:0;width:1px;height:1px;visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const bottom = probe.getBoundingClientRect().bottom;
  probe.remove();
  return Math.round(bottom);
}

let reports = 0;
let lastKey = '';

export function initIosViewport(): void {
  if (typeof window === 'undefined' || !isIos() || !isStandalone()) return;
  const root = document.documentElement;
  root.classList.add('ios-standalone');
  let applied = -1;
  const apply = (reason: string) => {
    if (!document.body) return 0;
    const s = safeInsets();
    // Игра заходит под вырез — iOS размывает этот край, шапка опускается ниже размытия
    root.classList.toggle('ios-under-notch', s.top > 0);
    const bottom = fixedBottom();
    const gap = iosViewportGap(bottom, window.innerWidth, screen.width, screen.height, s.top);
    if (gap !== applied) {
      applied = gap;
      root.style.setProperty('--sk-ios-gap', `${gap}px`);
      if (gap) root.style.setProperty('--sk-dvh', `${bottom + gap}px`);
      else root.style.removeProperty('--sk-dvh');
    }
    // Диагностика на сервер (журнал «Ошибки игроков»): каждый новый вариант размеров, не больше 8 за запуск
    const key = `${bottom}/${window.innerHeight}/${Math.round(window.visualViewport?.height ?? 0)}/${s.top}/${gap}`;
    if (key !== lastKey && reports < 8 && reason !== 'start') {
      lastKey = key;
      reports++;
      reportError(
        `ios-viewport#${reports} ${reason} fixedBottom=${bottom} inner=${window.innerWidth}x${window.innerHeight} ` +
          `screen=${screen.width}x${screen.height} vv=${Math.round(window.visualViewport?.height ?? 0)}` +
          `+${Math.round(window.visualViewport?.offsetTop ?? 0)} doc=${root.clientHeight} ` +
          `safeTop=${s.top} safeBottom=${s.bottom} gap=${gap}`,
      );
    }
    return gap;
  };
  apply('start');
  window.addEventListener('resize', () => apply('resize'));
  window.visualViewport?.addEventListener('resize', () => apply('vv-resize'));
  window.addEventListener('orientationchange', () => setTimeout(() => apply('orientation'), 300));
  // Вернулись из фона — iOS могла поменять высоту окна без события resize
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible')
      for (const ms of [0, 400, 1500]) setTimeout(() => apply('resume'), ms);
  });
  window.addEventListener('pageshow', () => apply('pageshow'));
  // Размеры и вырез iOS уточняет не сразу; дальше — дешёвая проверка раз в 3 с на случай тихих изменений
  for (const ms of [300, 1000, 2500]) setTimeout(() => apply('launch'), ms);
  window.setInterval(() => apply('tick'), 3000);
}
